import { ref, watch } from "vue";
import type {
  DialogConfig,
  GridSubmitGate,
  GridSubmitGateContext,
} from "@/v2/framework/dialog/types";
import type { GridViewBlock } from "@/v2/framework/view/types";
import type { ApiResponse } from "@/v2/api/response";
import { ColumnIconType, FieldType } from "@/v2/framework/column/types";
import type { ColumnEntry, ColumnIcon } from "@/v2/framework/column/types";
import { fetchFeedTypeMappingsList } from "./utils/api";
import {
  applyFeedMappingLocalDelete,
  applyFeedMappingLocalUpdate,
  isFeedMappingRowVisible,
} from "./utils/gridMutations";
import { buildFeedMapStatusSelectOptions, buildFeedTypeSelectOptions } from "./utils/labels";
import {
  createFeedMappingRow,
  formatFeedMappingForPersist,
  validateFeedMappingItems,
} from "./utils/persist";
const iconPerValue = {
  0: {
    code: "void",
    color: "var(--nxgds-text-semantic-error-primary, #dd415c)",
  },
  1: {
    code: "check-e",
    color: "var(--nxgds-text-semantic-success-primary, #15a772)",
  },
};

function normalizeRowsForPersist(rows: Record<string, unknown>[]) {
  return rows.map((row) => formatFeedMappingForPersist(row));
}

function stableSerialize(mappings: unknown[]) {
  return JSON.stringify(mappings);
}

export function createFeedMappingSubmitGate(ctx: GridSubmitGateContext): GridSubmitGate {
  let snapshot = "";
  const hasSuccessfulLoadRef = ref(false);

  watch(
    () => ctx.hasLoadedOnce.value,
    (hasLoaded) => {
      if (hasLoaded && !hasSuccessfulLoadRef.value) {
        snapshot = stableSerialize(normalizeRowsForPersist(ctx.rows.value));
        hasSuccessfulLoadRef.value = true;
      }
    },
    { immediate: true },
  );

  return {
    hasSuccessfulLoad: () => hasSuccessfulLoadRef.value,
    isDirty: () => {
      if (!hasSuccessfulLoadRef.value) return false;
      return stableSerialize(normalizeRowsForPersist(ctx.rows.value)) !== snapshot;
    },
    validateForDialogSubmit: () =>
      validateFeedMappingItems(ctx.rows.value.filter((row) => row.status)),
    buildMergeFragment: () => {
      if (!hasSuccessfulLoadRef.value) return null;
      if (stableSerialize(normalizeRowsForPersist(ctx.rows.value)) === snapshot) return null;
      return { feedTypeMappings: normalizeRowsForPersist(ctx.rows.value) };
    },
    markClean: () => {
      snapshot = stableSerialize(normalizeRowsForPersist(ctx.rows.value));
    },
  };
}

export function addFeedMappingRow(ctx: GridSubmitGateContext): void {
  const item = createFeedMappingRow();
  ctx.rows.value.push({ ...item, id: item.uniqueId });
}

const deleteFeedMappingDialog: DialogConfig = {
  id: "delete-feed-mapping",
  title: "common.labels.delete",
  description: (row) => {
    const feedEntityId = row?.feedEntityId;
    const label = feedEntityId != null && feedEntityId !== "" ? String(feedEntityId) : "";
    return label
      ? `Are you sure you want to delete ${label}?`
      : "Are you sure you want to delete this feed mapping?";
  },
  layout: {
    type: "nxgds-dialog",
    actions: [
      { intent: "dismiss", label: "common.buttons.no", variant: "secondary" },
      { intent: "submit", label: "common.buttons.yes", variant: "danger" },
    ],
  },
  structure: {
    type: "flat",
    children: [],
  },
  refetchAfterSubmit: false,
  api: {
    submit: async (_data, dialogCtx) => {
      if (dialogCtx.row?.id != null && dialogCtx.deleteGridRow) {
        await dialogCtx.deleteGridRow(dialogCtx.row.id);
      }
    },
  },
};

export function buildFeedMappingGrid(
  resolveEntityId: () => string | number,
  getFeedTypeMappings: (
    entityId: string | number,
    filters?: Record<string, unknown>,
  ) => Promise<ApiResponse>,
): GridViewBlock {
  return {
    type: "grid",
    showPagination: false,
    isRowVisible: isFeedMappingRowVisible,
    dialogs: {
      delete: deleteFeedMappingDialog,
    },
    columns: [
      {
        key: "feedEntityId",
        defaultVisibility: true,
        title: "table_columns.feed_entity_id",
        fieldType: FieldType.InputCell,
        lockable: false,
        width: "1fr",
        rowEditable: (row) => !!row.newlyAdded,
      },
      {
        key: "feedType",
        defaultVisibility: true,
        title: "table_columns.feed_type",
        fieldType: FieldType.SelectCell,
        options: buildFeedTypeSelectOptions(),
        width: "1fr",
        rowEditable: (row) => !!row.newlyAdded,
      },
      {
        key: "source",
        defaultVisibility: true,
        title: "table_columns.source",
        fieldType: FieldType.TextCell,
        icon: {
          type: ColumnIconType.Main,
          formatter: (row: unknown) => {
            const item = row as { source: string };
            return iconPerValue[(item.source as unknown as keyof typeof iconPerValue) || 0];
          },
        } as ColumnIcon,
      },
      {
        key: "status",
        defaultVisibility: true,
        title: "table_columns.status",
        fieldType: FieldType.SelectCell,
        options: buildFeedMapStatusSelectOptions(),
        width: "1fr",
      },
      {
        key: "actions",
        defaultVisibility: true,
        configurableVisibility: false,
        title: "",
        fieldType: FieldType.ActionsCell,
        sticky: true,
        rowActions: [
          {
            icon: "trash-ng",
            label: "common.labels.delete",
            onClick: (ctx) => {
              ctx.openDialog("delete");
            },
          },
        ],
      },
    ] as ColumnEntry[],
    api: {
      get: async (params) => {
        const data = await fetchFeedTypeMappingsList(getFeedTypeMappings, resolveEntityId(), {
          isReadAfterWrite: params.isReadAfterWrite === true,
        });
        return { data };
      },
    },
    applyLocalUpdate: applyFeedMappingLocalUpdate,
    applyLocalDelete: applyFeedMappingLocalDelete,
  };
}
